<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getVoiceCallProvider,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import {
  VOICE_CALL_DIRECTION,
  VOICE_CALL_OUTBOUND_INIT_STATUS,
} from 'dashboard/components-next/message/constants';
import { useWhatsappCallSession } from 'dashboard/composables/useWhatsappCallSession';
import { useCallsStore } from 'dashboard/stores/calls';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsappCallPaidNoticeDialog from 'dashboard/components-next/call/WhatsappCallPaidNoticeDialog.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const callsStore = useCallsStore();
const whatsappCallSession = useWhatsappCallSession();
const { isCloudFeatureEnabled } = useAccount();
const currentUser = useMapGetter('getCurrentUser');

// This button is WhatsApp-only: other call-capable channels (e.g. Twilio
// Voice) have their own entry points and don't render this button.
const isWhatsappVoiceInbox = computed(
  () =>
    getVoiceCallProvider(props.inbox) === VOICE_CALL_PROVIDERS.WHATSAPP &&
    isCloudFeatureEnabled(FEATURE_FLAGS.CHANNEL_VOICE)
);

// Unassigned conversations keep today's behavior (anyone with inbox access
// can call); once assigned, only the assignee can place the call.
const assignee = computed(() => props.chat?.meta?.assignee);
const isAssignedToCurrentUser = computed(
  () => !assignee.value || assignee.value.id === currentUser.value?.id
);

const callButtonTooltip = computed(() =>
  isAssignedToCurrentUser.value
    ? t('CONVERSATION.HEADER.WHATSAPP_CALL')
    : t('CONVERSATION.HEADER.WHATSAPP_CALL_ASSIGNEE_ONLY')
);

const isCallButtonDisabled = computed(() => {
  if (!isAssignedToCurrentUser.value) return true;
  if (callsStore.hasActiveCall || callsStore.hasIncomingCall) return true;
  return whatsappCallSession.isInitiating.value;
});

const isCallButtonLoading = computed(
  () => whatsappCallSession.isInitiating.value
);

const paidNoticeDialogRef = ref(null);
const isPaidNoticeDialogOpen = ref(false);

const startWhatsappCall = async () => {
  if (whatsappCallSession.isInitiating.value) return;
  try {
    const response = await whatsappCallSession.initiateOutboundCall(
      props.chat.id
    );

    // Composable returns LOCKED when init is already in flight or a call is
    // active; soft no-op so a parallel click doesn't trigger a banner.
    if (response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED) return;
    // Permission template path returns no call id — show banner, no widget yet.
    if (!response?.id) {
      const status = response?.status;
      const messageKey =
        status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
          ? 'CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_PENDING'
          : 'CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_REQUESTED';
      useAlert(t(messageKey));
      return;
    }

    // Stay non-active until Meta delivers the connect webhook (sdp_answer);
    // flipping to active here would start the duration timer before pickup.
    callsStore.addCall({
      callSid: response.provider_call_id,
      callId: response.id,
      conversationId: props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      provider: VOICE_CALL_PROVIDERS.WHATSAPP,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.WHATSAPP_CALL_FAILED'));
  }
};

const onConfirmWhatsappCall = async () => {
  await startWhatsappCall();
  paidNoticeDialogRef.value?.close();
};

const startCall = () => {
  // Guards against a rapid second click: isCallButtonDisabled mirrors the
  // button's own :disabled state, and isPaidNoticeDialogOpen stops a second
  // click from calling showModal() on an already-open dialog.
  if (isCallButtonDisabled.value || isPaidNoticeDialogOpen.value) return;
  paidNoticeDialogRef.value?.open();
};
</script>

<template>
  <template v-if="isWhatsappVoiceInbox">
    <NextButton
      v-tooltip.bottom="callButtonTooltip"
      sm
      ghost
      green
      icon="i-lucide-phone"
      :is-loading="isCallButtonLoading"
      :disabled="isCallButtonDisabled"
      @click="startCall"
    />
    <WhatsappCallPaidNoticeDialog
      ref="paidNoticeDialogRef"
      :is-loading="whatsappCallSession.isInitiating.value"
      @open="isPaidNoticeDialogOpen = true"
      @close="isPaidNoticeDialogOpen = false"
      @confirm="onConfirmWhatsappCall"
    />
  </template>
  <template v-else />
</template>
